import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import IRAValidation from './components/IRAValidation';
import ProductCard from './components/ProductCard';
import CoverDetails from './components/CoverDetails';
import InsuredDetails from './components/InsuredDetails';
import VehicleDetails from './components/VehicleDetails';
import Checkout from './components/Checkout';
import ExtendPolicyFinder from './components/ExtendPolicyFinder';
import Receipt from './components/Receipt';
import Snackbar from './components/Snackbar';
import Admin from './components/Admin';
import Agent from './components/Agent';
import AgentLanding from './components/AgentLanding';
import SignupPulse from './components/SignupPulse';
import EmployeeLogin from './components/EmployeeLogin';
import WhatsAppSupport from './components/WhatsAppSupport';
import GenerateQuoteButton from './components/GenerateQuoteButton';
import MainAppSidebar from './components/MainAppSidebar';
import { getApiUrl } from './config/api';
import { logoutAndNavigate } from './utils/logoutService';
import { buildPricingFromRates, amountFromTonnageRanges, grossFromNet } from './utils/productRatesApi';
import { initializeAnalytics, trackPageView } from './services/analyticsService';
import { fetchExtensionSeed, EXTENSION_SEED_USER_MESSAGE } from './utils/extensionSeed';
import {
  inferExtendableVehicleClass,
  resolveExtensionVehicleClass
} from './utils/extendPolicyInference';
import { tpoPricingLookupLabel } from './utils/tpoPricingLookup';
import { getInstitutionalPllFee } from './utils/institutionalPll';
import {
  TPO_EXTEND_VEHICLE_CLASSES,
  getTpoExtendAnnualPremiumFactor,
  mapAnnualTpoRowsToFreshExtendMonthDisplay,
  mapAnnualTpoRowsToSecondHalfExtendDisplay
} from './utils/tpoExtendRates';
import {
  inclusiveEndForCoverPeriod,
  compareYmd,
  getTodayYmdLocal,
  getTpoExtendEarliestStartYmd
} from './utils/calendarDates';
import { tpoPremiumLookupPeriod } from './utils/coverPeriodTerms';

const buildCheckoutTransactionKey = ({
  coverType,
  selectedClass,
  startDate,
  endDate,
  coverPeriod,
  insuredIdNumber,
  vehicleRegistrationNumber
}) => (
  [
    String(coverType || '').trim().toUpperCase(),
    String(selectedClass || '').trim().toUpperCase(),
    String(startDate || '').trim().slice(0, 10),
    String(endDate || '').trim().slice(0, 10),
    String(coverPeriod || '').trim().toUpperCase(),
    String(insuredIdNumber || '').trim().toUpperCase(),
    String(vehicleRegistrationNumber || '').trim().toUpperCase()
  ].join('|')
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [selectedCover, setSelectedCover] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [institutionType, setInstitutionType] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [coverPeriod, setCoverPeriod] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentStep, setCurrentStep] = useState('ira-validation');
  const [, setValidatedIRA] = useState(null);
  const [insuredData, setInsuredData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [insuranceCheckResult, setInsuranceCheckResult] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [receiptCertificateStatus, setReceiptCertificateStatus] = useState(null);
  const [receiptCertificateLastError, setReceiptCertificateLastError] = useState(null);
  const [extensionContext, setExtensionContext] = useState(null);
  const [extensionHydrationError, setExtensionHydrationError] = useState(null);
  const [appSection, setAppSection] = useState('products');
  
  // Snackbar state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  
  // Agent login state - must be at top level (before any returns)
  const [isAgentLoggedIn, setIsAgentLoggedIn] = useState(false);
  // Employee login state
  const [isEmployeeLoggedIn, setIsEmployeeLoggedIn] = useState(false);
  const [employeeUser, setEmployeeUser] = useState(null);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  // Show notification function
  const showNotification = (message, type = 'info', duration = 5000) => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setShowSnackbar(true);
  };

  // Manual retry function for failed saves
  const retryFailedSave = () => {
    const pendingData = localStorage.getItem('bimabot_pending_application');
    if (pendingData) {
      try {
        JSON.parse(pendingData); // validate format
        showNotification('Retrying to save your policy...', 'info');
        // This would need to be implemented to call the backend
        // For now, just show a notification
        showNotification('Retry functionality will be available soon', 'info');
      } catch (error) {
        showNotification('Unable to retry save. Please contact support.', 'error');
      }
    } else {
      showNotification('No pending data found to retry.', 'warning');
    }
  };

  // Scroll to top whenever step changes
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  // Google Analytics: init once and track route changes (SPA page views)
  useEffect(() => {
    initializeAnalytics();
  }, []);
  useEffect(() => {
    trackPageView(location.pathname || '/', document.title);
  }, [location.pathname]);
  useEffect(() => {
    const checkLogin = () => {
      const agentToken = localStorage.getItem('agentToken');
      const employeeToken = localStorage.getItem('employeeToken');
      const isEmployee = localStorage.getItem('isEmployee') === 'true';
      
      setIsAgentLoggedIn(!!agentToken && !isEmployee);
      setIsEmployeeLoggedIn(!!employeeToken && isEmployee);
      
      if (employeeToken && isEmployee) {
        const employeeUserStr = localStorage.getItem('employeeUser');
        if (employeeUserStr) {
          try {
            setEmployeeUser(JSON.parse(employeeUserStr));
          } catch (e) {
            console.error('Error parsing employee user:', e);
          }
        }
        // Ensure employees start at product selection (cover issuance landing page)
        if (currentStep === 'ira-validation') {
          setCurrentStep('product-selection');
        }
      }
    };
    
    // Check immediately
    checkLogin();
    
    // Also listen for storage changes (in case token is set/removed in another tab)
    window.addEventListener('storage', checkLogin);
    
    // Check periodically to catch changes
    const interval = setInterval(checkLogin, 1000);
    
    return () => {
      window.removeEventListener('storage', checkLogin);
      clearInterval(interval);
    };
  }, [currentStep]);

  const tpoClasses = [
    'Motor Private',
    'Motor Commercial Institutional',
    'Motor Commercial General Cartage',
    'Motor Commercial Own Goods',
    'Tractor TPO',
    'Tuk Tuk Commercial',
    'Tuk Tuk PSV',
    'Prime Movers',
    'Motorcycle Private',
    'Motorcycle PSV Annual',
    'Motorcycle PSV 6 Months',
    'PSV Chauffer Driven'
  ];
  const tpoExtendClasses = TPO_EXTEND_VEHICLE_CLASSES;

  const torClasses = [
    'Motor Private',
    'Motor Commercial',
    'PSV Chauffer Driven',
    'Motorcycle PSV'
  ];

  // Product rates: source of truth is /api/product-rates (product_rates in DB, synced from DMVIC Excel).
  // Fallback below matches current DMVIC rates (gross = customer pays) for initial paint / API failure.
  // Must be declared before tpoExtendPricing (useMemo depends on tpoPricing).
  const [tpoPricing, setTpoPricing] = useState([
    { label: 'Motor Private', amount: 3300, period: '1 Year' },
    { label: 'Motor Commercial Institutional', amount: 4155, period: '1 Year', tonnageBased: true },
    { label: 'Motor Commercial General Cartage', amount: 4155, period: '1 Year', tonnageBased: true },
    { label: 'Motor Commercial Own Goods', amount: 4155, period: '1 Year', tonnageBased: true },
    { label: 'Prime Movers', amount: 9185, period: '1 Year' },
    { label: 'Motorcycle Private', amount: 1870, period: '1 Year' },
    { label: 'Motorcycle PSV', amount: 3241, period: '1 Year' },
    { label: 'Motorcycle PSV', amount: 1870, period: '6 Months' },
    { label: 'Tractor TPO', amount: 2784, period: '1 Year' },
    { label: 'Tuk Tuk Commercial', amount: 2784, period: '1 Year' },
    { label: 'Tuk Tuk PSV', amount: 4155, period: '1 Year', psvPricing: true },
    { label: 'PSV Chauffer Driven', amount: 5500, period: '1 Year', psvPricing: true }
  ]);
  const [torPricing, setTorPricing] = useState([
    { label: 'Motor Private', amount: 600, period: '1 Month' },
    { label: 'Motor Commercial', amount: 600, period: '1 Month' },
    { label: 'PSV Chauffer Driven', amount: 1000, period: '1 Month' },
    { label: 'Motorcycle PSV', amount: 503, period: '1 Month' }
  ]);

  const tpoExtendPricing = React.useMemo(
    () => mapAnnualTpoRowsToSecondHalfExtendDisplay(tpoPricing, tpoExtendClasses),
    [tpoPricing, tpoExtendClasses]
  );

  const tpoExtendFreshMonthPricing = React.useMemo(
    () => mapAnnualTpoRowsToFreshExtendMonthDisplay(tpoPricing, tpoExtendClasses),
    [tpoPricing, tpoExtendClasses]
  );

  useEffect(() => {
    fetch(getApiUrl('/api/product-rates'))
      .then(res => res.json())
      .then(data => {
        if (data.success && data.rates && data.rates.length > 0) {
          const built = buildPricingFromRates(data.rates);
          if (built && (built.tpoPricing.length > 0 || built.torPricing.length > 0)) {
            if (built.tpoPricing.length > 0) setTpoPricing(built.tpoPricing);
            if (built.torPricing.length > 0) setTorPricing(built.torPricing);
          }
        }
      })
      .catch(() => {});
  }, []);

  // Function to calculate PSV pricing (TPO 1 Year). PLL = 500 per passenger.
  const calculatePSVPricing = (baseAmount, passengerCount) => {
    const n = parseInt(passengerCount, 10) || 0;
    if (n <= 0) return baseAmount;
    return baseAmount + (n * 500);
  };

  // Fallback Motor Commercial pricing (DMVIC tonnage bands, gross). Prefer API tonnage_ranges when available.
  const calculateCommercialPricing = (tonnage, bodyType = null) => {
    const tonnageValue = parseFloat(tonnage);
    if (bodyType && bodyType.toUpperCase() === 'PRIME MOVER') {
      return 9185; // DMVIC Prime Movers (rate includes levies = client pays)
    }
    // DMVIC bands: amounts include levies = what client pays (no conversion)
    if (tonnageValue <= 3) return 4155;
    if (tonnageValue <= 8) return 5070;
    if (tonnageValue <= 12) return 5984;
    if (tonnageValue <= 15) return 6899;
    if (tonnageValue <= 20) return 9185;
    return 13758;
  };

  // Calculate end date based on start date and period
  const calculateEndDate = (start, period) => inclusiveEndForCoverPeriod(start, period);

  // Update end date when start date or period changes (TPO_EXTEND: end pinned to original policy expiry).
  React.useEffect(() => {
    if (selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate) {
      setEndDate(extensionContext.annualEndDate);
      return;
    }
    if (startDate && coverPeriod) {
      const calculatedEndDate = calculateEndDate(startDate, coverPeriod);
      setEndDate(calculatedEndDate);
    }
  }, [startDate, coverPeriod, selectedCover, extensionContext?.annualEndDate]);

  // Internal pricing calculation function
  const calculatePremium = (coverType, selectedClass, coverPeriod, tonnage = null, passengerCount = null, bodyType = null, institutionTypeArg = null) => {
    const isExtendProduct = coverType === 'TPO_EXTEND';
    const isSecondHalfExtension = isExtendProduct && !!extensionContext?.policyNo;
    let pricingData = coverType === 'TPO' || isExtendProduct ? tpoPricing : torPricing;
    let premiumAmountFactor = 1;

    if (coverType === 'TPO' || isExtendProduct) {
      let lookupPeriod;
      if (isExtendProduct) {
        pricingData = isSecondHalfExtension ? tpoExtendPricing : tpoExtendFreshMonthPricing;
        lookupPeriod = isSecondHalfExtension ? '1 Year (50%)' : '1 Month';
        premiumAmountFactor = 1;
      } else {
        premiumAmountFactor = getTpoExtendAnnualPremiumFactor(coverType);
        lookupPeriod = tpoPremiumLookupPeriod(coverType, coverPeriod, selectedClass, {});
      }
      const rateLabel = tpoPricingLookupLabel(selectedClass);
      let matchingPrice = pricingData.find(
        (price) => price.label === rateLabel && price.period === lookupPeriod
      );
      // Never silently price 6 Months from the annual tariff — that remaps period/end to 1 Year.
      if (!matchingPrice && isExtendProduct) {
        const annualRow = tpoPricing.find(
          (price) => price.label === rateLabel && price.period === '1 Year'
        );
        if (annualRow) {
          matchingPrice = {
            ...annualRow,
            period: lookupPeriod,
            amount: Math.round(Number(annualRow.amount || 0) * 0.5)
          };
        }
      }
      
      // User-selected (or extend-fixed) period wins — never return product_rates.period of a fallback row.
      const pricedPeriod = isSecondHalfExtension
        ? '1 Year (50%)'
        : isExtendProduct
          ? '1 Month'
          : lookupPeriod;

      if (matchingPrice && matchingPrice.tonnageBased && (tonnage != null && tonnage !== '' || (selectedClass === 'Motor Commercial Institutional' && passengerCount != null))) {
        // Tonnage-based: use API tonnage_ranges if available, else fallback to hardcoded bands.
        // Motor Commercial Institutional: base 5000 (≤14 pax) or 7500 (>14 pax) GROSS + PLL 250/pax (DMVIC: amounts are already gross, no levies).
        const tonnagePremiumFactor = isExtendProduct
          ? getTpoExtendAnnualPremiumFactor('TPO_EXTEND')
          : premiumAmountFactor;
        const coverTypeForTonnage = matchingPrice.coverType || coverType;
        const fromApi = matchingPrice.tonnageRanges && amountFromTonnageRanges(matchingPrice.tonnageRanges, tonnage, coverTypeForTonnage, matchingPrice.isNet);
        const tonnageAmount = fromApi != null ? fromApi : calculateCommercialPricing(tonnage, bodyType);
        const nPax = parseInt(passengerCount, 10) || 0;
        const pllFee = getInstitutionalPllFee(institutionTypeArg);
        const institutionalBase = selectedClass === 'Motor Commercial Institutional'
          ? (nPax > 14 ? 7500 : 5000)
          : 0;
        const institutionalPll = selectedClass === 'Motor Commercial Institutional'
          ? nPax * pllFee
          : 0;
        const grossAnnual = (selectedClass === 'Motor Commercial Institutional'
          ? (institutionalBase + institutionalPll)
          : (tonnageAmount + institutionalPll));
        return {
          amount: Math.round(grossAnnual * tonnagePremiumFactor),
            period: pricedPeriod,
            description: selectedClass,
            tonnageBased: selectedClass !== 'Motor Commercial Institutional',
          tonnage: tonnage,
          bodyType: bodyType,
          ...(selectedClass === 'Motor Commercial Institutional' && passengerCount != null && { passengerCount, institutionalPllFee: pllFee, institutionType: institutionTypeArg || 'school' })
        };
      }
      
      if (matchingPrice && matchingPrice.psvPricing) {
        let feePerPax = matchingPrice.passengerFee != null ? Number(matchingPrice.passengerFee) : 0;
        if (feePerPax === 0 && selectedClass === 'PSV Chauffer Driven') feePerPax = 500;
        const n = parseInt(passengerCount, 10) || 0;
        const annualAmount = matchingPrice.amount + n * feePerPax;
        if (feePerPax <= 0) {
          return {
            amount: Math.round(Number(matchingPrice.amount || 0) * premiumAmountFactor),
            period: pricedPeriod,
            description: selectedClass,
            psvPricing: true,
            passengerCount,
            baseAmount: matchingPrice.amount,
            passengerFee: 0,
            passengerFeePerPax: 0
          };
        }
        const psvAmount = Math.round(annualAmount * premiumAmountFactor);
        return {
          amount: psvAmount,
          period: pricedPeriod,
          description: selectedClass,
          psvPricing: true,
          passengerCount: passengerCount,
          baseAmount: matchingPrice.amount,
          passengerFee: n * feePerPax,
          passengerFeePerPax: feePerPax
        };
      }
      
      return matchingPrice
        ? {
            ...matchingPrice,
            amount: Math.round(Number(matchingPrice.amount || 0) * premiumAmountFactor),
            period: pricedPeriod
          }
        : {
            amount: 0,
            period: pricedPeriod,
            description: selectedClass,
            rateMissing: true
          };
    } else {
      // TOR: add PLL when rate has passengerFee (e.g. PSV Chauffer Driven 100/pax)
      const matchingPrice = pricingData.find(price => price.label === selectedClass);
      if (!matchingPrice) return { amount: 0, period: '1 Month', description: selectedClass };
      const baseAmount = matchingPrice.amount ?? 0;
      const pllFee = matchingPrice.passengerFee != null ? Number(matchingPrice.passengerFee) : 0;
      const n = parseInt(passengerCount, 10) || 0;
      const total = pllFee > 0 && n > 0 ? baseAmount + (n * pllFee) : baseAmount;
      return {
        amount: total,
        period: '1 Month',
        description: selectedClass,
        baseAmount,
        passengerFee: pllFee > 0 ? n * pllFee : 0,
        passengerCount: n,
        passengerFeePerPax: pllFee
      };
    }
  };

  const handleCoverSelection = (cover) => {
    // Product cards always start a clean quote. Finder sets extensionContext via handleProceedFromCoreSearch (not this handler).
    setExtensionContext(null);
    setSelectedCover(cover);
    setAppSection(cover === 'TPO_EXTEND' ? 'extend-policy' : 'products');
    if (cover === 'TPO_EXTEND') {
      setCoverPeriod('1 Month');
      setStartDate('');
      setEndDate('');
      setSelectedClass(null);
      setInstitutionType(null);
      setInsuredData(null);
      setVehicleData(null);
    } else if (cover === 'TPO') {
      setCoverPeriod('1 Year');
      setStartDate('');
      setEndDate('');
      setSelectedClass(null);
      setInstitutionType(null);
      setInsuredData(null);
      setVehicleData(null);
    } else if (cover === 'TOR') {
      setCoverPeriod('1 Month');
      setStartDate('');
      setEndDate('');
      setSelectedClass(null);
      setInstitutionType(null);
      setInsuredData(null);
      setVehicleData(null);
    }
    setCurrentStep('cover-details');
  };

  const handleCoverDetailsComplete = (classData, startDateData, periodData, institutionTypeData = null) => {
    setSelectedClass(classData);
    setInstitutionType(classData === 'Motor Commercial Institutional' ? institutionTypeData : null);
    setStartDate(startDateData);
    setCoverPeriod(periodData);
    const calculatedEndDate =
      selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate
        ? extensionContext.annualEndDate
        : calculateEndDate(startDateData, periodData);
    setEndDate(calculatedEndDate);
    if (selectedCover === 'TPO_EXTEND' && extensionContext?.policyNo) {
      setCurrentStep('extension-loading');
      return;
    }
    setCurrentStep('insured-details');
  };

  const handleInsuredDetailsComplete = (insuredDetails) => {
    setInsuredData(insuredDetails);
    setCurrentStep('vehicle-details');
  };

  const handleVehicleDetailsComplete = (vehicleDetails, insuranceResult, nextAvailableDate = null) => {
    const withInstitution =
      selectedClass === 'Motor Commercial Institutional' && institutionType
        ? { ...vehicleDetails, institutionType }
        : vehicleDetails;
    setVehicleData(withInstitution);
    setInsuranceCheckResult(insuranceResult);
    
    // If user confirmed to use next available date, update start date
    if (nextAvailableDate) {
      setStartDate(nextAvailableDate);
      if (coverPeriod) {
        if (selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate) {
          setEndDate(extensionContext.annualEndDate);
        } else {
          const calculatedEndDate = calculateEndDate(nextAvailableDate, coverPeriod);
          setEndDate(calculatedEndDate);
        }
      }
    }
    
    setCurrentStep('checkout');
  };

  const handleProceedFromCoreSearch = async ({
    registrationNumber: _registrationNumber,
    inferredClass,
    startDate: transactionStartDate,
    selectedPolicy
  }) => {
    const sourcePolicyNo = selectedPolicy?.policyNo || selectedPolicy?.policy_number || null;
    if (!sourcePolicyNo) {
      showNotification(EXTENSION_SEED_USER_MESSAGE, 'error');
      throw new Error(EXTENSION_SEED_USER_MESSAGE);
    }
    const seed = await fetchExtensionSeed(getApiUrl, sourcePolicyNo);
    const portalAnnualEnd =
      (seed.computedAnnualEndDate && String(seed.computedAnnualEndDate).trim()) ||
      (seed.firstSegmentStartDate && inclusiveEndForCoverPeriod(seed.firstSegmentStartDate, '1 Year')) ||
      '';
    if (!portalAnnualEnd) {
      const msg =
        'This policy has no recorded first-segment start date in the portal, so the annual policy end cannot be computed (first issuance + 1 year). Extensions cannot continue until that record exists or Underwriting confirms the dates.';
      showNotification(msg, 'error', 16000);
      throw new Error(msg);
    }
    const earliestStart = getTpoExtendEarliestStartYmd(seed.firstSegmentStartDate);
    if (compareYmd(earliestStart, portalAnnualEnd) > 0) {
      const msg =
        'The annual policy end on file is before the earliest allowed extension start. This extension cannot continue in the app until Underwriting corrects the policy dates.';
      showNotification(msg, 'error', 14000);
      throw new Error(msg);
    }

    const coreInferred =
      inferredClass || inferExtendableVehicleClass(selectedPolicy) || null;
    const extensionVehicleClass = resolveExtensionVehicleClass(
      seed.firstSegmentVehicleClass,
      coreInferred,
      tpoExtendClasses
    );

    setSelectedCover('TPO_EXTEND');
    setAppSection('extend-policy');
    setSelectedClass(extensionVehicleClass);
    // Prefer calendar earliest (today when first-segment cert has expired). Only honor finder start if it is on/after earliest.
    const fromFinder = transactionStartDate || null;
    const candidate =
      fromFinder && compareYmd(fromFinder, earliestStart) >= 0 ? fromFinder : earliestStart;
    const initialStart =
      compareYmd(candidate, portalAnnualEnd) > 0 ? portalAnnualEnd : candidate;
    setStartDate(initialStart);
    setCoverPeriod('1 Year (50%)');
    setEndDate(portalAnnualEnd);
    setInsuredData(seed.insuredData);
    const seededInstitutionType =
      extensionVehicleClass === 'Motor Commercial Institutional' ? seed.institutionType || null : null;
    setInstitutionType(seededInstitutionType);
    setVehicleData(
      seededInstitutionType
        ? { ...seed.vehicleData, institutionType: seededInstitutionType }
        : seed.vehicleData
    );
    setExtensionHydrationError(null);
    setExtensionContext({
      policyNo: sourcePolicyNo,
      sourcePolicy: selectedPolicy || null,
      seedClientId: seed.clientId || null,
      annualEndDate: portalAnnualEnd,
      firstSegmentStartDate: seed.firstSegmentStartDate || null,
      firstSegmentVehicleClass: seed.firstSegmentVehicleClass || extensionVehicleClass
    });
    setCurrentStep('cover-details');
  };

  useEffect(() => {
    if (currentStep !== 'extension-loading' || !extensionContext?.policyNo) return;
    let cancelled = false;
    const hydrate = async () => {
      try {
        const seed = await fetchExtensionSeed(getApiUrl, extensionContext.policyNo);
        if (cancelled) return;
        let seededVehicle = seed.vehicleData;
        const registrationNumber = seededVehicle?.registrationNumber || vehicleData?.registrationNumber;
        if (registrationNumber) {
          const vsRes = await fetch(getApiUrl('/api/dmvic/search-vehicle'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ registrationNumber })
          });
          const vs = await vsRes.json().catch(() => null);
          if (!cancelled && vsRes.ok && vs?.success && vs?.vehicle) {
            const v = vs.vehicle;
            seededVehicle = {
              ...(seededVehicle || {}),
              registrationNumber: (v.VehicleRegistrationNumber && String(v.VehicleRegistrationNumber).trim()) || seededVehicle?.registrationNumber || registrationNumber,
              chassisNumber: (v.ChassisNumber && String(v.ChassisNumber).trim()) || seededVehicle?.chassisNumber || '',
              engineNumber: (v.EngineNumber && String(v.EngineNumber).trim()) || seededVehicle?.engineNumber || ''
            };
          }
        }
        if (cancelled) return;
        setInsuredData(seed.insuredData);
        setVehicleData(seededVehicle || seed.vehicleData);
        const portalAnnualFromSeed =
          (seed.computedAnnualEndDate && String(seed.computedAnnualEndDate).trim()) ||
          (seed.firstSegmentStartDate && inclusiveEndForCoverPeriod(seed.firstSegmentStartDate, '1 Year')) ||
          '';
        if (seed.firstSegmentVehicleClass) {
          setSelectedClass(
            resolveExtensionVehicleClass(
              seed.firstSegmentVehicleClass,
              extensionContext?.firstSegmentVehicleClass || selectedClass,
              tpoExtendClasses
            )
          );
        }
        setExtensionContext((prev) =>
          prev && String(prev.policyNo || '') === String(seed.policyNo || '')
            ? {
                ...prev,
                seedClientId: seed.clientId || prev.seedClientId,
                firstSegmentStartDate: seed.firstSegmentStartDate || prev.firstSegmentStartDate,
                firstSegmentVehicleClass:
                  seed.firstSegmentVehicleClass || prev.firstSegmentVehicleClass,
                annualEndDate: portalAnnualFromSeed || prev.annualEndDate
              }
            : prev
        );
        if (portalAnnualFromSeed) {
          setEndDate(portalAnnualFromSeed);
        }

        const vdForDi = seededVehicle || seed.vehicleData || {};
        const regDi = String(vdForDi.registrationNumber || '').trim();
        const chDi = String(vdForDi.chassisNumber || '').trim();
        const startDi = String(startDate || '').trim().slice(0, 10);
        const endDi = String(portalAnnualFromSeed || extensionContext?.annualEndDate || '').trim().slice(0, 10);
        if (regDi && startDi && endDi && !chDi) {
          if (cancelled) return;
          setExtensionHydrationError(
            'Chassis number is required for overlap check and issuance. It was not returned from NTSA for this registration — try again or contact support.'
          );
          return;
        }
        if (regDi && chDi && startDi && endDi) {
          const diRes = await fetch(getApiUrl('/api/dmvic/validate-double-insurance'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vehicleData: { chassisNumber: chDi, registrationNumber: regDi },
              policyDates: { startDate: startDi, endDate: endDi }
            })
          });
          const di = await diRes.json().catch(() => ({}));
          if (cancelled) return;
          if (diRes.ok && di.success && di.hasDoubleInsurance && !di.canProceed) {
            const hint = di.nextAvailableDate ? ` You may start on or after ${String(di.nextAvailableDate).slice(0, 10)}.` : '';
            setExtensionHydrationError(
              `${di.message || 'Double insurance was detected for this vehicle over the extension window.'}${hint} Adjust the extension start in Cover Details or resolve the conflicting policy.`
            );
            return;
          }
        }

        setExtensionHydrationError(null);
        setCurrentStep('checkout');
      } catch (e) {
        if (cancelled) return;
        setExtensionHydrationError(e.message || EXTENSION_SEED_USER_MESSAGE);
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, [currentStep, extensionContext?.policyNo, extensionContext?.annualEndDate, startDate]);

  // When user chooses "Choose my own date" on double insurance: save vehicle + insurance, go to Cover Details to pick start date
  const handleSaveVehicleAndGoToCoverDetails = (vehicleDetails, insuranceResult) => {
    setVehicleData(vehicleDetails);
    setInsuranceCheckResult(insuranceResult);
    setCurrentStep('cover-details');
  };

  // Checkout calls this with { clientId, policyNumber } after it has already saved the application and issued the certificate; use that and skip duplicate submit.
  const handlePaymentComplete = async (resultFromCheckout) => {
    try {
      if (resultFromCheckout?.clientId) {
        setClientId(resultFromCheckout.clientId);
        setCurrentStep('summary');
        return;
      }
      // Fallback: no clientId from Checkout (legacy or edge case) – submit once
      const finalPremium = calculatePremium(selectedCover, selectedClass, coverPeriod, vehicleData?.tonnage, vehicleData?.licensedToCarry, vehicleData?.bodyType, institutionType || vehicleData?.institutionType);
      const response = await fetch(getApiUrl('/api/submit-application'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coverType: selectedCover,
          vehicleClass: selectedClass,
          startDate: startDate,
          coverPeriod: coverPeriod,
          endDate: endDate,
          premiumAmount: finalPremium.amount,
          insuredData: insuredData,
          vehicleDetails: vehicleData
        })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setClientId(result.clientId);
      setCurrentStep('summary');
    } catch (error) {
      showNotification('Failed to save application. Please try again.', 'error');
    }
  };

  const resetToStart = () => {
    setSelectedCover(null);
    setSelectedClass(null);
    setInstitutionType(null);
    setStartDate('');
    setCoverPeriod('');
    setEndDate('');
    setCurrentStep('ira-validation');
    setValidatedIRA(null);
    setInsuredData(null);
    setVehicleData(null);
    setInsuranceCheckResult(null);
    setClientId(null);
    setExtensionContext(null);
    setExtensionHydrationError(null);
    localStorage.removeItem('bimabot_validated_ira');
  };

  const handleIRAValidationSuccess = (iraData) => {
    setValidatedIRA(iraData);
    setCurrentStep('product-selection');
  };

  // Handle view switching - removed old switchToMain, using React Router navigate instead

  // Navigation functions for jumping to specific sections
  // const navigateToSection = (section) => {
  //   setCurrentStep(section);
  // };

  // Check for validated IRA on mount
  useEffect(() => {
    const storedIRA = localStorage.getItem('bimabot_validated_ira');
    if (storedIRA) {
      try {
        const iraData = JSON.parse(storedIRA);
        setValidatedIRA(iraData);
        if (currentStep === 'ira-validation') {
          setCurrentStep('product-selection');
        }
      } catch (error) {
        console.error('Error parsing stored IRA data:', error);
        localStorage.removeItem('bimabot_validated_ira');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open receipt in new tab: when URL has view=receipt&clientId&policyNumber (e.g. from "View receipt" link), fetch receipt data and show summary
  useEffect(() => {
    const pathname = location.pathname || '';
    if (!pathname.startsWith('/insurance')) return;
    const params = new URLSearchParams(location.search || '');
    const view = params.get('view');
    const cId = params.get('clientId');
    const pNum = params.get('policyNumber');
    if (view !== 'receipt' || !cId) return;
    let cancelled = false;
    fetch(getApiUrl(`/api/receipt-info?clientId=${encodeURIComponent(cId)}`))
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data.success || !data.clientId) return;
        setClientId(data.clientId);
        setSelectedCover(data.coverType || null);
        setSelectedClass(data.selectedClass || null);
        setStartDate(data.startDate || '');
        setEndDate(data.endDate || '');
        setCoverPeriod(data.coverPeriod || '');
        setInsuredData(data.insuredData || null);
        setVehicleData(data.vehicleData || null);
        setReceiptCertificateStatus(data.certificateStatus || null);
        setReceiptCertificateLastError(data.certificateLastError || null);
        setCurrentStep('summary');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname, location.search]);

  const renderCurrentStep = () => {
    // Employees should skip IRA validation and go directly to product selection
    if (isEmployeeLoggedIn && currentStep === 'ira-validation') {
      setCurrentStep('product-selection');
      return null;
    }

    switch (currentStep) {
      case 'ira-validation':
        return (
          <IRAValidation
            onValidationSuccess={handleIRAValidationSuccess}
          />
        );
      
      case 'product-selection':
        return (
          <div className="product-selection">
            <div className="enhanced-header">
              <h1 className="enhanced-title">{appSection === 'extend-policy' ? 'Buy/Extend Policy' : 'Choose Your Insurance Cover'}</h1>
            </div>
            <div className="product-cards">
              {appSection === 'extend-policy' ? (
                <div style={{ width: '100%', maxWidth: 720, margin: '0 auto' }}>
                  <ExtendPolicyFinder onProceed={handleProceedFromCoreSearch} />
                  <div style={{ marginTop: 16 }}>
                    <ProductCard
                      title="Start a new extendable cover purchase"
                      subtitle="New quote — not the second 50% on an existing portal policy"
                      features={[
                        'Use when you do not yet have a policy number from this app for this vehicle',
                        'You will enter vehicle and insured details manually; DMVIC search is still available later'
                      ]}
                      onClick={() => handleCoverSelection('TPO_EXTEND')}
                      icon="📋"
                      isPremium={false}
                      variant="extend"
                      buttonLabel="Get Cover"
                    />
                  </div>
                </div>
              ) : (
                <>
              <ProductCard
                title="Third Party Only Insurance"
                subtitle="TPO Cover"
                features={[
                  "Third Party Liability Coverage",
                  "PLL for PSV & Commercial",
                  "Affordable Premiums"
                ]}
                onClick={() => handleCoverSelection('TPO')}
                icon="🛡️"
                isPremium={false}
              />
              <ProductCard
                title="Time on Risk Insurance"
                subtitle="TOR Cover"
                features={[
                  "Third Party Liability Coverage",
                  "PLL for PSV & Commercial",
                  "Short period rates"
                ]}
                onClick={() => handleCoverSelection('TOR')}
                icon="⏰"
                isPremium={false}
              />
                </>
              )}
            </div>
          </div>
        );

      case 'extension-finder':
        return (
          <div className="product-selection">
            <div className="enhanced-header">
              <h1 className="enhanced-title">Buy/Extend Policy</h1>
            </div>
            <ExtendPolicyFinder onProceed={handleProceedFromCoreSearch} />
          </div>
        );
      
      case 'cover-details':
        return (
          <CoverDetails
            key={`cover-details-${selectedCover}`}
            coverType={selectedCover}
            classes={selectedCover === 'TPO' ? tpoClasses : selectedCover === 'TPO_EXTEND' ? tpoExtendClasses : torClasses}
            pricing={
              selectedCover === 'TOR'
                ? torPricing
                : selectedCover === 'TPO_EXTEND' && !extensionContext?.annualEndDate
                  ? tpoExtendFreshMonthPricing
                  : selectedCover === 'TPO_EXTEND'
                    ? tpoExtendPricing
                    : tpoPricing
            }
            initialData={{ selectedClass, startDate, coverPeriod, endDate, institutionType }}
            onComplete={handleCoverDetailsComplete}
            onBack={() => setCurrentStep(selectedCover === 'TPO_EXTEND' ? 'product-selection' : 'product-selection')}
            fixedPeriod={
              selectedCover === 'TPO_EXTEND'
                ? extensionContext?.annualEndDate
                  ? '1 Year (50%)'
                  : '1 Month'
                : null
            }
            lockFields={selectedCover === 'TPO_EXTEND' && !!extensionContext}
            continueLabel={selectedCover === 'TPO_EXTEND' && !!extensionContext ? 'Continue to Checkout →' : 'Continue to Insured Details →'}
            extensionAnnualEndDate={selectedCover === 'TPO_EXTEND' ? extensionContext?.annualEndDate : null}
            extensionFirstSegmentStartDate={
              selectedCover === 'TPO_EXTEND' ? extensionContext?.firstSegmentStartDate : null
            }
          />
        );
      
      case 'insured-details':
        return (
          <InsuredDetails
            coverType={selectedCover}
            selectedClass={selectedClass}
            startDate={startDate}
            coverPeriod={coverPeriod}
            endDate={endDate}
            initialData={insuredData}
            onComplete={handleInsuredDetailsComplete}
            onBack={() => setCurrentStep('cover-details')}
          />
        );
      
      case 'vehicle-details':
        return (
          <VehicleDetails
            coverType={selectedCover}
            selectedClass={selectedClass}
            institutionType={institutionType}
            startDate={startDate}
            endDate={endDate}
            coverPeriod={coverPeriod}
            pricing={
              selectedCover === 'TOR'
                ? torPricing
                : selectedCover === 'TPO_EXTEND' && !extensionContext?.annualEndDate
                  ? tpoExtendFreshMonthPricing
                  : selectedCover === 'TPO_EXTEND'
                    ? tpoExtendPricing
                    : tpoPricing
            }
            initialData={vehicleData}
            onComplete={handleVehicleDetailsComplete}
            onBack={() => setCurrentStep('insured-details')}
            onBackToCoverDetails={() => setCurrentStep('cover-details')}
            onSaveAndGoToCoverDetails={handleSaveVehicleAndGoToCoverDetails}
            extensionAnnualEndDate={selectedCover === 'TPO_EXTEND' ? extensionContext?.annualEndDate : null}
          />
        );
      
      case 'checkout': {
        const checkoutPremium = calculatePremium(selectedCover, selectedClass, coverPeriod, vehicleData?.tonnage, vehicleData?.licensedToCarry, vehicleData?.bodyType, institutionType || vehicleData?.institutionType);
        // Pin period/end to Cover Details selection — never let premium.period rewrite 6 Months → 1 Year.
        const checkoutCoverPeriod = coverPeriod;
        const checkoutEndDate =
          selectedCover === 'TPO_EXTEND' && extensionContext?.annualEndDate
            ? extensionContext.annualEndDate
            : startDate && coverPeriod
              ? calculateEndDate(startDate, coverPeriod)
              : endDate;
        const extensionSeedBlocked =
          selectedCover === 'TPO_EXTEND' &&
          !!extensionContext?.policyNo &&
          (!insuredData?.idNumber || !String(vehicleData?.registrationNumber || '').trim());
        return (
          <Checkout
            coverType={selectedCover}
            selectedClass={selectedClass}
            institutionType={institutionType || vehicleData?.institutionType || null}
            startDate={startDate}
            endDate={checkoutEndDate}
            coverPeriod={checkoutCoverPeriod}
            insuredData={insuredData}
            vehicleData={vehicleData}
            premium={checkoutPremium}
            insuranceCheckResult={insuranceCheckResult}
            onBack={() => setCurrentStep(selectedCover === 'TPO_EXTEND' ? 'cover-details' : 'vehicle-details')}
            onPaymentComplete={handlePaymentComplete}
            extensionContext={extensionContext}
            extensionSeedBlocked={extensionSeedBlocked}
          />
        );
      }

      case 'extension-loading':
        return (
          <div className="cover-details" style={{ textAlign: 'center', padding: '60px 20px', maxWidth: 560, margin: '0 auto' }}>
            <h2 style={{ marginBottom: 12 }}>{extensionHydrationError ? 'Could not load extension data' : 'Loading extension data...'}</h2>
            {!extensionHydrationError && (
              <>
                <p style={{ marginBottom: 10, color: '#556' }}>
                  Fetching insured and vehicle details from our records for checkout.
                </p>
                <div style={{ width: 36, height: 36, margin: '0 auto', border: '4px solid #d8e6df', borderTopColor: '#1B4837', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </>
            )}
            {extensionHydrationError && (
              <p style={{ marginTop: 16, color: '#5d1a1a', fontSize: 14, lineHeight: 1.5, textAlign: 'left' }}>
                {extensionHydrationError}
              </p>
            )}
            {extensionHydrationError && (
              <button
                type="button"
                className="btn-primary"
                style={{ marginTop: 20 }}
                onClick={() => {
                  setExtensionHydrationError(null);
                  setCurrentStep('cover-details');
                }}
              >
                Back to cover details
              </button>
            )}
          </div>
        );
      
      case 'summary':
        const summaryPremium = calculatePremium(selectedCover, selectedClass, coverPeriod, vehicleData?.tonnage, vehicleData?.licensedToCarry, vehicleData?.bodyType, institutionType || vehicleData?.institutionType);
        const handleRetryCertificate = () => {
          try {
            const ref = typeof localStorage !== 'undefined' && (localStorage.getItem('agentbima_payment_reference') || localStorage.getItem('bimabot_payment_reference'));
            if (typeof sessionStorage !== 'undefined' && clientId) {
              const transactionKey = buildCheckoutTransactionKey({
                coverType: selectedCover,
                selectedClass,
                startDate,
                endDate,
                coverPeriod,
                insuredIdNumber: insuredData?.idNumber,
                vehicleRegistrationNumber: vehicleData?.registrationNumber
              });
              sessionStorage.setItem('agentbima_cert_failed', JSON.stringify({
                clientId,
                failedJobId: null,
                error: receiptCertificateLastError || 'Certificate issuance failed.',
                referenceNumber: ref || null,
                transactionKey,
                ts: Date.now()
              }));
            }
            setCurrentStep('checkout');
          } catch (_) {}
        };
        return (
          <Receipt
            coverType={selectedCover}
            selectedClass={selectedClass}
            startDate={startDate}
            endDate={endDate}
            coverPeriod={coverPeriod}
            insuredData={insuredData}
            vehicleData={vehicleData}
            premium={summaryPremium}
            clientId={clientId}
            certificateStatus={receiptCertificateStatus}
            certificateLastError={receiptCertificateLastError}
            onNewApplication={resetToStart}
            onRetrySave={retryFailedSave}
            onRetryCertificate={handleRetryCertificate}
          />
        );
      
      default:
        return null;
    }
  };

  // Switch to main view function (for navigation)
  const switchToMain = () => {
    navigate('/');
    // If agent is logged in, skip IRA validation and go to product selection
    const agentToken = localStorage.getItem('agentToken');
    if (agentToken) {
      setCurrentStep('product-selection');
      // Set a dummy validated IRA so the flow continues
      setValidatedIRA({ 
        iraNumber: 'AGENT',
        agencyName: 'Agent Account',
        isAgent: true 
      });
    } else {
      // Reset to IRA validation for regular clients
      setCurrentStep('ira-validation');
    }
  };

  // Handle employee login - redirect to main page after login
  const handleEmployeeLogin = (userData) => {
    // Employee login handled by EmployeeLogin component
    // It stores tokens and redirects to main page
    navigate('/');
  };

  // Handle agent logout
  const handleAgentLogout = async () => {
    setIsAgentLoggedIn(false);
    
    await logoutAndNavigate({
      userType: 'agent',
      navigate,
      redirectPath: '/',
      showNotificationFn: showNotification
    });
  };

  // Handle employee logout
  const handleEmployeeLogout = async () => {
    setIsEmployeeLoggedIn(false);
    setEmployeeUser(null);
    setCurrentStep('product-selection');
    
    await logoutAndNavigate({
      userType: 'employee',
      navigate,
      redirectPath: '/',
      showNotificationFn: showNotification
    });
  };

  // Navigate to agent dashboard
  const navigateToAgentDashboard = () => {
    navigate('/agent');
  };

  const resetFlowForSection = (section) => {
    setAppSection(section);
    setSelectedCover(null);
    setSelectedClass(null);
    setStartDate('');
    setCoverPeriod('');
    setEndDate('');
    setInsuredData(null);
    setVehicleData(null);
    setInsuranceCheckResult(null);
    setExtensionContext(null);
    setExtensionHydrationError(null);
    setCurrentStep('product-selection');
  };

  const handleMainSidebarSection = (section) => {
    if (section === 'extend-policy') {
      resetFlowForSection('extend-policy');
      return;
    }
    if (section === 'home' || section === 'products') {
      resetFlowForSection('products');
    }
  };

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.main-app-sidebar') && !event.target.closest('.mobile-menu-btn')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Main BIMABOT view component
  const MainBIMABOTView = () => (
    <div className="App">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar Navigation - Only show when user is logged in */}
      {(isAgentLoggedIn || isEmployeeLoggedIn) && (
        <MainAppSidebar
          isMobileOpen={isMobileMenuOpen}
          onMobileClose={() => setIsMobileMenuOpen(false)}
          isAgentLoggedIn={isAgentLoggedIn}
          isEmployeeLoggedIn={isEmployeeLoggedIn}
          employeeUser={employeeUser}
          activeSection={appSection === 'extend-policy' ? 'extend-policy' : 'products'}
          onNavigateToSection={handleMainSidebarSection}
          onNavigateToAgentDashboard={navigateToAgentDashboard}
          onAgentLogout={handleAgentLogout}
          onEmployeeLogout={handleEmployeeLogout}
        />
      )}

      {/* Header - Only show when user is logged in */}
      {(isAgentLoggedIn || isEmployeeLoggedIn) && (
        <header className="app-header">
          <div className="header-content">
            <div className="header-left">
              {/* Mobile Menu Button */}
              <button 
                className="mobile-menu-btn"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="menu-icon">☰</span>
              </button>
              
              <div className="company-title">
                <h1>The Monarch Insurance Company</h1>
              </div>
            </div>
          
          <div className="header-right">
            {currentStep !== 'ira-validation' && currentStep !== 'product-selection' && (
              <div className="progress-indicator">
                <span className="step">Step {currentStep === 'cover-details' ? 2 : 
                  currentStep === 'insured-details' ? 3 : 
                  currentStep === 'vehicle-details' ? 4 : 
                  currentStep === 'checkout' ? 5 : 6} of 6</span>
              </div>
            )}
          </div>
        </div>
      </header>
      )}
      
      <main className="app-main">
        {renderCurrentStep()}
      </main>
      
      {/* Snackbar Notifications */}
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={5000}
        />
      )}
      
      {/* Floating Buttons - Only show on product selection and later steps, but not for employees */}
      {(currentStep === 'product-selection' || currentStep === 'cover-details' || currentStep === 'insured-details' || currentStep === 'vehicle-details' || currentStep === 'checkout') && !isEmployeeLoggedIn && (
        <>
          <GenerateQuoteButton />
          <WhatsAppSupport variant="floating" />
        </>
      )}
    </div>
  );

  // Route protection: redirect to landing (/) if not logged in and trying to access a protected route
  const pathname = location.pathname;
  const isPublicPath =
    pathname === '/' ||
    pathname.startsWith('/agent') ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/pulse') ||
    pathname === '/employee';
  const hasAuth =
    !!localStorage.getItem('agentToken') || !!localStorage.getItem('employeeToken');
  if (!isPublicPath && !hasAuth) {
    return <Navigate to="/" replace />;
  }

  // Use React Router Routes
  // IMPORTANT: Order matters - more specific routes must come before less specific ones
  return (
    <Routes>
      {/* Agent Portal Routes - Must come before "/" */}
      {/* Using /agent/* to match /agent and all sub-paths */}
      <Route path="/agent/*" element={<Agent onBackToMain={switchToMain} />} />
      
      {/* Admin Portal Routes */}
      <Route path="/admin/*" element={<Admin onBackToMain={switchToMain} />} />
      
      <Route path="/pulse/signups/:token" element={<SignupPulse />} />

      {/* Employee Login Route */}
      <Route path="/employee" element={<EmployeeLogin onLogin={handleEmployeeLogin} onBackToMain={switchToMain} />} />
      
      {/* Main BIMABOT Route for insurance purchase flow - requires login */}
      <Route path="/insurance/*" element={<MainBIMABOTView />} />
      
      {/* Landing Page (default) - Shows Sign In/Sign Up options */}
      <Route path="/" element={<AgentLanding />} />
      
      {/* Catch-all: unknown paths redirect to landing when not authenticated */}
      <Route path="*" element={hasAuth ? <Navigate to="/insurance" replace /> : <Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
